import { AuditReport } from '../digital-audit-constants';
import { CommentAnchor } from '../comments-constants';
import { CommentThread } from './CommentThread';
import { DetectionHit } from '../engine/martech-scanner-constants';
import { scanOriginLabel } from '../digital-audit-utils';
import { insightMore } from './audit-report-utils';
import { ObservationCard } from './ObservationCard';

interface AuditReportViewProps {
  report: AuditReport | null;
}

const SEVERITY_ORDER: Record<string, number> = { RISK: 0, OPPORTUNITY: 1, GAP: 1, NOTE: 2 };

/** A platform's overall origin is the union of all its individual hits' seenIn. */
const chipOriginLabel = (hits: DetectionHit[]): string | null =>
  scanOriginLabel(hits.flatMap(h => h.seenIn ?? []));

/** Foundation-observation topics that the scanner's own insights may already cover. */
const FOUNDATION_TOPIC_PATTERNS = [/server.side tagging/i, /consent management/i, /\bcdp\b/i];

const foundationTopic = (title: string): RegExp | undefined =>
  FOUNDATION_TOPIC_PATTERNS.find(pattern => pattern.test(title));

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

export const AuditReportView = ({ report }: AuditReportViewProps) => {
  if (!report) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>Run a scan to see the audit report.</div>
      </div>
    );
  }

  const { scanResult, journey } = report;
  const { detections, extractedIds, advertiserInsights, headlessAssessment, accessChecklist } =
    scanResult;
  const categories = Object.entries(detections).filter(
    ([, platforms]) => Object.keys(platforms).length > 0
  );
  const platformCount = categories.reduce((acc, [, p]) => acc + Object.keys(p).length, 0);
  const idEntries = Object.entries(extractedIds);
  const clientName = journey.clientName || scanResult.domain;

  // Merge scanner insights with journey foundation observations, skipping
  // foundation topics the scanner already reported (ported from render_print_html).
  const coveredTopics = new Set(
    advertiserInsights.map(i => foundationTopic(i.title)).filter(Boolean)
  );
  const foundationObs = journey.foundationObservations.filter(
    o => !coveredTopics.has(foundationTopic(o.title))
  );
  const observations = [...advertiserInsights, ...foundationObs].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
  );

  return (
    <div className='digital-audit-page'>
      <div className='digital-audit-arch-banner'>
        <div className='digital-audit-arch-eyebrow'>
          Architecture · confidence {headlessAssessment.confidence}
        </div>
        <div className='digital-audit-arch-name'>{clientName}</div>
        <div className='digital-audit-arch-verdict'>
          <b>{headlessAssessment.verdict}</b>
          {headlessAssessment.signals.length > 0 && (
            <> · {headlessAssessment.signals.join(' · ')}</>
          )}
        </div>
        <div className='digital-audit-arch-net'>
          {scanResult.domain} · {scanResult.pagesScanned.length} page(s) scanned
          {scanResult.pageMetrics &&
            scanResult.pageMetrics.length > 0 &&
            ` · median server response ${median(scanResult.pageMetrics.map(m => m.headerMs))} ms`}
          {scanResult.runtimeNetwork &&
            ` · ${scanResult.runtimeNetwork.thirdPartyHosts.length} third-party hosts`}
        </div>
        {scanResult.errors.length > 0 && (
          <div className='digital-audit-scan-notes'>
            {scanResult.errors.map(err => (
              <div key={err} className='digital-audit-scan-note'>
                ⚠ {err}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>Detected stack</div>
          <div className='digital-audit-section-count'>
            {platformCount} platforms on {scanResult.domain}
          </div>
        </div>
        <div className='digital-audit-stack-grid'>
          {categories.map(([category, platforms]) => (
            <div key={category} className='digital-audit-category-group'>
              <div className='digital-audit-category-label'>{category}</div>
              <div className='digital-audit-chip-list'>
                {Object.entries(platforms).map(([platform, hits]) => {
                  const originLabel = chipOriginLabel(hits);
                  return (
                    <span key={platform} className='digital-audit-chip'>
                      {platform}
                      {originLabel && (
                        <span className='digital-audit-chip-origin'> · {originLabel}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <CommentThread anchor={CommentAnchor.DETECTED_STACK} />
      </div>

      {idEntries.length > 0 && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Key IDs</div>
          </div>
          <table className='digital-audit-table'>
            <tbody>
              {idEntries.flatMap(([idType, values]) =>
                values.map(value => (
                  <tr key={`${idType}-${value}`}>
                    <td className='digital-audit-table-cat'>{idType}</td>
                    <td className='digital-audit-table-mono'>{value}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <CommentThread anchor={CommentAnchor.KEY_IDS} />
        </div>
      )}

      {(observations.length > 0 || journey.redundancies.length > 0) && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Observations &amp; opportunities</div>
            <div className='digital-audit-section-count'>
              {observations.length + journey.redundancies.length} findings — expand for detail
            </div>
          </div>
          {observations.length > 0 && (
            <div>
              <span className='digital-audit-obs-group-label'>Stack observations</span>
              <div className='digital-audit-obs-list'>
                {observations.map(o => (
                  <ObservationCard
                    key={o.title}
                    severity={o.severity}
                    title={o.title}
                    note={o.note}
                    more={insightMore(o)}
                  />
                ))}
              </div>
            </div>
          )}
          {journey.redundancies.length > 0 && (
            <div>
              <span className='digital-audit-obs-group-label'>Consolidation candidates</span>
              <div className='digital-audit-obs-list'>
                {journey.redundancies.map(r => (
                  <ObservationCard
                    key={r.cap}
                    severity='OVERLAP'
                    title={`${r.label}: ${r.tools.join(', ')}`}
                    note={r.note}
                    more={r.more}
                  />
                ))}
              </div>
            </div>
          )}
          <CommentThread anchor={CommentAnchor.OBSERVATIONS} />
        </div>
      )}

      {accessChecklist.length > 0 && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Access to request</div>
            <div className='digital-audit-section-count'>onboarding checklist</div>
          </div>
          <table className='digital-audit-table'>
            <thead>
              <tr>
                <th aria-label='Checkbox column' className='digital-audit-checkbox-cell' />
                <th>Access</th>
                <th>Detected via</th>
                <th>How it&apos;s granted</th>
              </tr>
            </thead>
            <tbody>
              {accessChecklist.map(item => (
                <tr key={item.request}>
                  <td className='digital-audit-checkbox-cell'>☐</td>
                  <td className='digital-audit-chk-label'>{item.request}</td>
                  <td>{item.platform}</td>
                  <td className='digital-audit-obs-note'>{item.how}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <CommentThread anchor={CommentAnchor.ACCESS_TO_REQUEST} />
        </div>
      )}
    </div>
  );
};
